import {
  cloneBoggleLetterBags,
  drawLettersForClass,
  type BoggleLetterBags,
  type ClassifiedLetterWeights,
} from './letterBag'
import { nextInteger, nextRandom } from './rng'
import type { GridDimensions, LetterBoard } from './types'

export type LetterWeight = readonly [string, number]

export type OpeningVowelPlacementOptions = {
  consonantWeights: ReadonlyArray<LetterWeight>
  maxVowelRatioExclusive: number
  minVowelRatio: number
  vowelWeights: ReadonlyArray<LetterWeight>
}

/** Smallest and largest vowel counts that satisfy `isValidOpeningBoard`-style rules for a full grid. */
export function getFeasibleOpeningVowelCountBounds(
  totalCells: number,
  minVowelRatio: number,
  maxVowelRatioExclusive: number,
): { max: number; min: number } | null {
  if (totalCells <= 0) {
    return null
  }

  let minFeasible: null | number = null
  let maxFeasible: null | number = null

  for (let vowels = 0; vowels <= totalCells; vowels += 1) {
    const consonants = totalCells - vowels
    const ratio = vowels / totalCells
    if (
      consonants > vowels &&
      ratio >= minVowelRatio &&
      ratio < maxVowelRatioExclusive
    ) {
      minFeasible ??= vowels
      maxFeasible = vowels
    }
  }

  if (minFeasible === null || maxFeasible === null) {
    return null
  }

  return { max: maxFeasible, min: minFeasible }
}

function pickOpeningBoardVowelCount(
  totalCells: number,
  minVowelRatio: number,
  maxVowelRatioExclusive: number,
  rngState: number,
): { rngState: number; vowelCount: number } {
  const bounds = getFeasibleOpeningVowelCountBounds(
    totalCells,
    minVowelRatio,
    maxVowelRatioExclusive,
  )

  if (bounds === null) {
    return { rngState, vowelCount: 0 }
  }

  const scaled = nextRandom(rngState)
  const ratioBandWidth = Math.max(0, maxVowelRatioExclusive - minVowelRatio)
  const sampledRatio =
    ratioBandWidth > 0
      ? minVowelRatio + scaled.value * ratioBandWidth
      : minVowelRatio + scaled.value * Number.EPSILON

  let vowelCount = Math.round(sampledRatio * totalCells)
  vowelCount = Math.min(bounds.max, Math.max(bounds.min, vowelCount))

  return {
    rngState: scaled.rngState,
    vowelCount,
  }
}

function shuffleIndexOrder(length: number, rngState: number): { order: number[]; rngState: number } {
  const order = Array.from({ length }, (_, index) => index)
  let current = rngState

  for (let index = length - 1; index > 0; index -= 1) {
    const rolled = nextInteger(current, index + 1)
    current = rolled.rngState
    const swapIndex = rolled.value
    ;[order[index], order[swapIndex]] = [order[swapIndex]!, order[index]!]
  }

  return { order, rngState: current }
}

function weightPoolHasPositiveWeight(weights: ReadonlyArray<LetterWeight>) {
  return weights.some(([, weight]) => weight > 0)
}

export function fillLetterBoardFromBags(
  dimensions: GridDimensions,
  rngState: number,
  nextCellId: number,
  letterBags: BoggleLetterBags,
  classified: ClassifiedLetterWeights,
  opening: OpeningVowelPlacementOptions,
): {
  board: LetterBoard
  letterBags: BoggleLetterBags
  nextCellId: number
  rngState: number
} {
  const cellCount = dimensions.rows * dimensions.cols
  const picked = pickOpeningBoardVowelCount(
    cellCount,
    opening.minVowelRatio,
    opening.maxVowelRatioExclusive,
    rngState,
  )
  let currentRng = picked.rngState
  const vowelCount = picked.vowelCount
  const consonantCount = cellCount - vowelCount

  const shuffled = shuffleIndexOrder(cellCount, currentRng)
  currentRng = shuffled.rngState
  const vowelFlatIndices = shuffled.order.slice(0, vowelCount)
  const consonantFlatIndices = shuffled.order.slice(vowelCount)

  let currentBags = letterBags
  const vowelPick = drawLettersForClass(
    currentBags,
    'vowels',
    vowelCount,
    classified,
    currentRng,
  )
  currentBags = vowelPick.bags
  currentRng = vowelPick.rngState

  const consonantPick = drawLettersForClass(
    currentBags,
    'consonants',
    consonantCount,
    classified,
    currentRng,
  )
  currentBags = consonantPick.bags
  currentRng = consonantPick.rngState

  const flatLetters: string[] = new Array<string>(cellCount)
  for (let index = 0; index < vowelCount; index += 1) {
    const flat = vowelFlatIndices[index]!
    flatLetters[flat] = vowelPick.letters[index]!
  }
  for (let index = 0; index < consonantCount; index += 1) {
    const flat = consonantFlatIndices[index]!
    flatLetters[flat] = consonantPick.letters[index]!
  }

  const board: LetterBoard = Array.from({ length: dimensions.rows }, () => [])
  let assignId = nextCellId

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const flat = row * dimensions.cols + col
      board[row]?.push({
        id: assignId,
        letter: flatLetters[flat]!,
        tags: undefined,
      })
      assignId += 1
    }
  }

  return {
    board,
    letterBags: currentBags,
    nextCellId: assignId,
    rngState: currentRng,
  }
}

export { cloneBoggleLetterBags }
